import { useSendGpioCommandMutation } from "@/core/cameraApi";
import { useTypedAppFormContext } from "@/core/form";
import { useCameraApiUrl } from "@/hooks";
import { useAppSelector } from "@/hooks/redux";
import { selectActiveCameraMxid } from "@/modules/camera-selection/services/cameraSelectionSlice";
import { Button } from "@/modules/shadcn/ui/button";
import { EvalGpioStateEnum } from "@repo/schema";
import { ZapIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { gpioStateLabel } from "../../utils/limitFormatters";
import {
  GPIO_PINS,
  isGpioAction,
  type ActionFieldsProps,
} from "./actionTypes";
import { evalActionsFormOptions } from "./evalActionsForm.options";

export function GpioActionFields({ index, actions }: ActionFieldsProps) {
  const { t } = useTranslation(["evaluation", "common"]);
  const form = useTypedAppFormContext({ ...evalActionsFormOptions });
  const [sendGpioCommand, { isLoading }] = useSendGpioCommandMutation();

  // The project's camera, the same one a deploy drives the pin from — NOT the
  // dashboard configuration's `cameraMxid`. That column stopped being written
  // when the camera selector moved to the project modal, so the value left in
  // it is whatever camera the config was last saved against; on a relay-backed
  // project that is a camera the device has never heard of and the command came
  // back 404 "Camera not found". A config created since then has none at all,
  // which disabled the button outright.
  const cameraMxid = useAppSelector(selectActiveCameraMxid);
  // A relay-backed project has no base URL until its session cookie exists;
  // sending before then resolves against nothing and fails opaquely.
  const cameraApiUrl = useCameraApiUrl();
  const canTest = Boolean(cameraMxid && cameraApiUrl);

  // A pin may carry only one action, so offer this row's own pin plus any pin no
  // other row has claimed.
  const claimedElsewhere = new Set(
    actions
      .filter((action, i) => i !== index && isGpioAction(action))
      .map((action) => (isGpioAction(action) ? action.pin : 0)),
  );
  const pinOptions = GPIO_PINS.filter(
    (pin) => !claimedElsewhere.has(pin),
  ).map((pin) => ({ value: String(pin), label: t("gpio.pinOption", { pin }) }));
  const stateOptions = Object.values(EvalGpioStateEnum).map((state) => ({
    value: state,
    label: t(gpioStateLabel[state]),
  }));

  // Read at click time rather than in render, so the command always reflects
  // what the row currently shows.
  const handleTest = async () => {
    const action = form.state.values.actions[index];
    if (!cameraMxid || !action || !isGpioAction(action)) return;

    try {
      await sendGpioCommand({
        mxid: cameraMxid,
        pin: action.pin,
        state: action.state,
        durationMs: action.durationMs,
      }).unwrap();
      toast.success(
        t("gpio.toast.driven", {
          pin: action.pin,
          state: t(gpioStateLabel[action.state]),
        }),
      );
    } catch (error) {
      const status = (error as { status?: unknown })?.status;
      toast.error(
        status === 409
          ? t("gpio.toast.notBooted")
          : status === 404
            ? t("gpio.toast.notFound")
            : t("gpio.toast.failed"),
      );
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-28">
        <form.AppField name={`actions[${index}].pin`}>
          {(field) => (
            <field.SelectInput
              label={t("gpio.pin")}
              options={pinOptions}
              parseValue={(v) => Number(v)}
              formatValue={(v) => String(v ?? "")}
            />
          )}
        </form.AppField>
      </div>

      <div className="w-32">
        <form.AppField name={`actions[${index}].state`}>
          {(field) => (
            <field.SelectInput label={t("gpio.state")} options={stateOptions} />
          )}
        </form.AppField>
      </div>

      <div className="w-44">
        <form.AppField name={`actions[${index}].durationMs`}>
          {(field) => (
            <field.NumberInput
              label={t("gpio.holdFor")}
              unit="ms"
              placeholder={t("gpio.latch")}
            />
          )}
        </form.AppField>
      </div>

      <span
        title={
          canTest
            ? undefined
            : cameraMxid
              ? t("gpio.connecting")
              : t("gpio.selectCamera")
        }
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canTest || isLoading}
          onClick={handleTest}
        >
          <ZapIcon className="size-4" />
          {t("gpio.test")}
        </Button>
      </span>

      <p className="w-full text-xs text-muted-foreground">{t("gpio.hint")}</p>
    </div>
  );
}
